/**
 * Where the Overall graph draws everything, for a measured width: one column per stage spread
 * evenly across the width (the first at the left edge, the last ending at the right), each column
 * right under its title and centred on the tallest one, and every edge with an arrowhead. Between
 * stages an edge is a soft curve from a card's right side to the next card's left side; down the
 * Delivery column it is a short drop from one card to the one below, or, past a card, a bracket on
 * the column's left. The graph is laid out at its real size (zoom 1), so type keeps its size and
 * the canvas is exactly as tall as its content. Pure, so positions and paths are tested.
 *
 * An edge that skips a stage (a project with changes queued but no agent live) runs through a free
 * gap of the column it crosses, never through a card.
 */
import { FLOW_STAGES } from './flow.ts';
import type { Flow, FlowNode, FlowState } from './flow.ts';

/** A node card's height (the two-line row). */
export const NODE_H = 56;
/** The space between cards in a column: room for a drop with its arrowhead. */
export const ROW_GAP = 20;
/** The stage titles above the columns. */
export const HEAD_H = 28;
/** Below this width the stages stack instead (a phone). */
export const MIN_GRAPH_WIDTH = 560;
/** An arrowhead's length; a line stops this short of the card it points at. */
export const ARROW = 6;
/** Room under the last card for React Flow's attribution: its link keeps a 24 px circle clear of every card (axe target-size). */
export const FOOT_H = 20;
/** Extra space under the Next card, so what is lined up reads apart from the projects below it. */
export const NEXT_GAP = 12;
/** How far a bracket stands off a column's left side. */
const BRACKET = 10;

export interface LaidColumn {
  index: number;
  title: string;
  /** The stage's size before folding (FlowColumn totals). */
  total: number;
  x: number;
  w: number;
}

export interface LaidNode {
  node: FlowNode;
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface LaidEdge {
  /** `${from}>${to}`: the key events use to find the line a dot travels. */
  id: string;
  from: string;
  to: string;
  state: FlowState;
  active: boolean;
  /** across: right side to left side; down: bottom to top; bracket: left side round to left side. */
  route: 'across' | 'down' | 'bracket';
  /** The SVG path, in canvas coordinates, ending ARROW short of its target. */
  d: string;
  /** The arrowhead: its tip and the way it points. */
  arrow: { x: number; y: number; dir: 'right' | 'down' };
  /** The columns it crosses without stopping. */
  skips: number[];
}

export interface FlowLayout {
  width: number;
  height: number;
  gap: number;
  columns: LaidColumn[];
  nodes: LaidNode[];
  edges: LaidEdge[];
}

const r1 = (n: number) => Math.round(n * 10) / 10;
const pt = (x: number, y: number) => `${r1(x)} ${r1(y)}`;

/** A soft S curve from (sx, sy) to (tx, ty), leaving and arriving level. */
export function curve(sx: number, sy: number, tx: number, ty: number): string {
  const k = (tx - sx) / 2;
  return `M ${pt(sx, sy)} C ${pt(sx + k, sy)} ${pt(tx - k, ty)} ${pt(tx, ty)}`;
}

/** A small filled arrowhead with its tip at (x, y). */
export function arrowHead(x: number, y: number, dir: 'right' | 'down'): string {
  const w = ARROW * 0.6;
  return dir === 'right' ? `M ${pt(x, y)} L ${pt(x - ARROW, y - w)} L ${pt(x - ARROW, y + w)} Z` : `M ${pt(x, y)} L ${pt(x - w, y - ARROW)} L ${pt(x + w, y - ARROW)} Z`;
}

export function layoutFlow(flow: Flow, width: number): FlowLayout {
  const gap = Math.min(72, Math.max(32, Math.round(width * 0.055)));
  const n = FLOW_STAGES.length;
  const w = r1(Math.max(0, width - gap * (n - 1)) / n);
  const byColumn = FLOW_STAGES.map((_, c) => flow.nodes.filter((x) => x.column === c));
  // The work cards at the top of a column (Up next, Missions) sit a little apart from the projects under them.
  const isWork = (x: FlowNode | undefined) => x?.kind === 'next' || x?.kind === 'missions';
  const workCount = (list: readonly FlowNode[]) => list.filter(isWork).length;
  const apart = (list: readonly FlowNode[]) => (workCount(list) > 0 && list.length > workCount(list) ? NEXT_GAP : 0);
  const span = (list: readonly FlowNode[]) => Math.max(1, list.length) * NODE_H + Math.max(0, list.length - 1) * ROW_GAP + apart(list);
  const body = Math.max(...byColumn.map(span));

  const columns: LaidColumn[] = FLOW_STAGES.map((stage, c) => ({ index: c, title: stage.title, total: flow.totals[c] ?? 0, x: r1(c * (w + gap)), w }));

  const nodes: LaidNode[] = [];
  const at = new Map<string, LaidNode>();
  byColumn.forEach((list, c) => {
    const top = HEAD_H + (body - span(list)) / 2;
    list.forEach((node, i) => {
      const laid = { node, x: columns[c]!.x, y: top + i * (NODE_H + ROW_GAP) + (i >= workCount(list) && i > 0 ? apart(list) : 0), w, h: NODE_H };
      nodes.push(laid);
      at.set(node.id, laid);
    });
  });

  const edges: LaidEdge[] = [];
  let below = false;
  for (const e of flow.edges) {
    const a = at.get(e.from);
    const b = at.get(e.to);
    if (!a || !b) continue;
    const ca = a.node.column;
    const cb = b.node.column;
    const base = { id: `${e.from}>${e.to}`, from: e.from, to: e.to, state: e.state, active: e.active };
    if (ca === cb) {
      // Down a column: a drop to the card right below, or a bracket on the left past the cards between.
      if (b.y <= a.y) continue;
      const next = Math.abs(b.y - (a.y + NODE_H + ROW_GAP)) < 0.5;
      if (next) {
        const x = a.x + a.w / 2;
        edges.push({ ...base, route: 'down', d: `M ${pt(x, a.y + a.h)} L ${pt(x, b.y - ARROW)}`, arrow: { x, y: b.y, dir: 'down' }, skips: [] });
      } else {
        const x = a.x - BRACKET;
        const sy = a.y + a.h / 2;
        const ty = b.y + b.h / 2;
        const r = 6;
        const d = `M ${pt(a.x, sy)} L ${pt(x + r, sy)} Q ${pt(x, sy)} ${pt(x, sy + r)} L ${pt(x, ty - r)} Q ${pt(x, ty)} ${pt(x + r, ty)} L ${pt(b.x - ARROW, ty)}`;
        edges.push({ ...base, route: 'bracket', d, arrow: { x: b.x, y: ty, dir: 'right' }, skips: [] });
      }
      continue;
    }
    // Lines between stages run left to right only: the stages are a sequence.
    if (cb < ca) continue;
    const sx = a.x + a.w;
    const sy = a.y + a.h / 2;
    const tx = b.x;
    const ty = b.y + b.h / 2;
    const skips = Array.from({ length: Math.max(0, cb - ca - 1) }, (_, i) => ca + 1 + i);
    const blocked = nodes.filter((x) => skips.includes(x.node.column));
    let d: string;
    if (!blocked.length) d = curve(sx, sy, tx - ARROW, ty);
    else {
      const lane = laneThrough(blocked, (sy + ty) / 2);
      if (lane > HEAD_H + body) below = true;
      const xs = columns[skips[0]!]!.x;
      const last = columns[skips[skips.length - 1]!]!;
      const xe = last.x + last.w;
      d = `${curve(sx, sy, xs, lane)} L ${pt(xe, lane)} ${curve(xe, lane, tx - ARROW, ty).replace(/^M [^C]+/, '')}`;
    }
    edges.push({ ...base, route: 'across', d, arrow: { x: tx, y: ty, dir: 'right' }, skips });
  }

  // Exactly as tall as the content: the titles and the tallest column, room for a lane run below it, and the attribution.
  return { width, height: HEAD_H + body + (below ? ROW_GAP : 0) + FOOT_H, gap, columns, nodes, edges };
}

/** The free horizontal lane nearest `want` past the cards in the way: a gap between them, or just above or below them. */
export function laneThrough(blocked: readonly LaidNode[], want: number): number {
  const free = (y: number) => blocked.every((n) => y < n.y - 2 || y > n.y + n.h + 2);
  const candidates = blocked.flatMap((n) => [n.y - ROW_GAP / 2, n.y + n.h + ROW_GAP / 2]).filter(free);
  candidates.sort((p, q) => Math.abs(p - want) - Math.abs(q - want));
  return candidates[0] ?? want;
}
